import { collection, deleteDoc, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { auth, db } from '../firebase';
import { copyAppLink as copyAppLinkToClipboard } from '../utils/copyAppLink';

const styles = {
  screen: { minHeight: '100vh', backgroundColor: '#F4F6F9', fontFamily: 'system-ui, sans-serif' },
  main: { marginLeft: 220, padding: 24 },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 },
  pageTitle: { fontSize: 22, fontWeight: '700', color: '#0A2D5A' },
  pageSub: { fontSize: 13, color: '#888', marginTop: 2 },
  addBtn: { backgroundColor: '#E8620A', color: 'white', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: '600', cursor: 'pointer' },
  controls: { display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' },
  searchInput: { padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13, width: 220, outline: 'none' },
  filterBtn: { padding: '7px 14px', borderRadius: 8, border: '1px solid #ddd', fontSize: 12, fontWeight: '600', cursor: 'pointer', backgroundColor: 'white', color: '#888' },
  filterBtnActive: { padding: '7px 14px', borderRadius: 8, border: '1px solid #1D6FA4', fontSize: 12, fontWeight: '600', cursor: 'pointer', backgroundColor: '#E6F1F9', color: '#1D6FA4' },
  legend: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12, fontSize: 12, color: '#666' },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6 },
  legendDot: { width: 9, height: 9, borderRadius: '50%', display: 'inline-block' },
  clientsCard: { backgroundColor: 'white', borderRadius: 12, border: '0.5px solid #e0e0e0', overflow: 'hidden' },
  clientRow: { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '0.5px solid #f0f0f0', gap: 12 },
  avatar: { width: 34, height: 34, borderRadius: '50%', backgroundColor: '#E6F1F9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: '600', color: '#1D6FA4', flexShrink: 0 },
  clientName: { fontSize: 15, fontWeight: '600', color: '#111', flex: 1, minWidth: 0 },
  clientSub: { fontSize: 12, color: '#888', marginTop: 1 },
  clientCode: { fontSize: 12, color: '#0A2D5A', marginTop: 2, fontWeight: '600' },
  clientCodeLabel: { color: '#888', fontWeight: '600' },
  clientCodeValue: { fontFamily: 'monospace', letterSpacing: 1, fontSize: 13, color: '#0A2D5A' },
  planBadge: { fontSize: 11, padding: '2px 10px', borderRadius: 10, fontWeight: '600', whiteSpace: 'nowrap' },
  installDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0, cursor: 'default' },
  emailDot: { width: 10, height: 10, borderRadius: '50%', flexShrink: 0, cursor: 'default', marginLeft: 4 },
  actionBtn: { padding: '7px 14px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, cursor: 'pointer', backgroundColor: 'white', color: '#444', fontWeight: '600' },
  deleteBtn: { padding: '5px 10px', borderRadius: 6, border: '1px solid #FEE2E2', fontSize: 11, cursor: 'pointer', backgroundColor: '#FEE2E2', color: '#CC0000' },
  // Modal styles
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalCard: { backgroundColor: 'white', borderRadius: 16, padding: 32, maxWidth: 400, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: 18, fontWeight: '700', color: '#0A2D5A', marginBottom: 8 },
  modalBody: { fontSize: 14, color: '#555', lineHeight: '22px', marginBottom: 24 },
  modalClientName: { fontWeight: '700', color: '#0A2D5A' },
  modalButtons: { display: 'flex', gap: 12, justifyContent: 'flex-end' },
  modalCancel: { padding: '10px 20px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, fontWeight: '600', cursor: 'pointer', backgroundColor: 'white', color: '#444' },
  modalConfirm: { padding: '10px 20px', borderRadius: 8, border: 'none', fontSize: 14, fontWeight: '600', cursor: 'pointer', backgroundColor: '#CC0000', color: 'white' },
  // Toast styles
  toast: { position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)', backgroundColor: '#0A2D5A', color: 'white', padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: '600', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 2000, display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' },
};

const STATUS_CONFIG = {
  staying:   { label: 'Staying',     bg: '#DCFCE7', color: '#166534', border: '#86EFAC' },
  scheduled: { label: 'Scheduled',   bg: '#DBEAFE', color: '#1E40AF', border: '#93C5FD' },
  noresponse:{ label: 'No Response', bg: '#FEE2E2', color: '#CC0000', border: '#FCA5A5' },
};

function AEPStatusBadge({ clientId, status, onStatusChange }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const insideTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const insideMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!insideTrigger && !insideMenu) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // The dropdown is portaled to document.body (see below) to escape
  // clientsCard's overflow:hidden, which otherwise clips it near the
  // bottom of the list. Close on resize rather than reposition, since
  // a resize is rare compared to scroll (which the portal already
  // tracks naturally via page-relative, not viewport-relative, offsets).
  useEffect(() => {
    if (!open) return;
    const handleResize = () => setOpen(false);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [open]);

  const toggleOpen = () => {
    if (!open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + window.scrollY + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setOpen(o => !o);
  };

  const current = STATUS_CONFIG[status] || null;

  return (
    <div>
      <div
        ref={triggerRef}
        onClick={toggleOpen}
        style={{
          display: 'inline-block', padding: '3px 10px', borderRadius: 10,
          fontSize: 11, fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
          backgroundColor: current ? current.bg : '#F4F6F9',
          color: current ? current.color : '#888',
          border: `1px solid ${current ? current.border : '#ddd'}`,
          minWidth: 90, textAlign: 'center',
        }}>
        {current ? current.label : 'Set Status'}
      </div>
      {open && menuPos && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'absolute', top: menuPos.top, right: menuPos.right, backgroundColor: 'white',
            border: '1px solid #e0e0e0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 1500, minWidth: 150, overflow: 'hidden',
          }}>
          {[
            { value: 'staying',    label: 'Staying',      ...STATUS_CONFIG.staying },
            { value: 'scheduled',  label: 'Scheduled',    ...STATUS_CONFIG.scheduled },
            { value: 'noresponse', label: 'No Response',  ...STATUS_CONFIG.noresponse },
            { value: '',           label: 'Clear Status',  bg: 'white', color: '#888', border: 'white' },
          ].map(opt => (
            <div
              key={opt.value}
              onClick={() => { onStatusChange(clientId, opt.value); setOpen(false); }}
              style={{ padding: '8px 14px', fontSize: 12, fontWeight: '600', cursor: 'pointer', color: opt.color, backgroundColor: opt.bg, borderBottom: '0.5px solid #f0f0f0' }}>
              {opt.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [confirmModal, setConfirmModal] = useState(null);
  const [toast, setToast] = useState(null);

  const agentId = auth.currentUser?.uid;

  useEffect(() => {
    fetchClients();
  }, [agentId]);

  const fetchClients = async () => {
    if (!agentId) return;
    const q = query(collection(db, 'clients'), where('agentId', '==', agentId));
    const snap = await getDocs(q);
    setClients(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    setLoading(false);
  };

  const handleStatusChange = async (clientId, newStatus) => {
    await updateDoc(doc(db, 'clients', clientId), { aepStatus: newStatus || null });
    setClients(prev => prev.map(c => c.id === clientId ? { ...c, aepStatus: newStatus || null } : c));
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 2500);
  };

  const copyAppLink = (lookupCode, clientName) => {
    const result = copyAppLinkToClipboard(lookupCode);
    if (!result.ok) {
      showToast('Code still generating — try again in a moment');
      return;
    }
    showToast(`✓  App link copied for ${clientName}`);
  };

  const handleDeleteConfirmed = async () => {
    if (!confirmModal) return;
    await deleteDoc(doc(db, 'clients', confirmModal.clientId));
    setClients(prev => prev.filter(c => c.id !== confirmModal.clientId));
    setConfirmModal(null);
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const exportClientsCSV = () => {
    const headers = ['Name','Mobile','State','Plan Type','Carrier','Plan','Premium','Start Date','Review Interval','Birthday Month','Birthday Day','AEP Status','Access Code','App Installed','Notes'];
    const rows = clients.map(c => [
      c.name || '', c.mobile || '', c.state || '', c.planType || '', c.carrier || '', c.plan || '',
      c.premium || '', c.startDate || '', c.reviewInterval || '', c.birthdayMonth || '', c.birthdayDay || '',
      c.aepStatus || '', c.lookupCode || '', c.appInstalled ? 'Yes' : 'No', (c.notes || '').replace(/[\r\n]+/g, ' '),
    ]);
    const escape = (val) => {
      const str = String(val);
      return /["\n,]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str;
    };
    const csv = [headers, ...rows].map(row => row.map(escape).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `medicare-plan-guardian-clients-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const filtered = clients.filter(c => {
    const matchSearch = c.name?.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || c.planType === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div style={styles.screen}>
      <Sidebar />
      <div style={styles.main}>

        {/* Toast */}
        {toast && (
          <div style={styles.toast}>
            {toast}
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmModal && (
          <div style={styles.modalOverlay}>
            <div style={styles.modalCard}>
              <div style={styles.modalTitle}>Remove Client</div>
              <div style={styles.modalBody}>
                Are you sure you want to remove <span style={styles.modalClientName}>{confirmModal.name}</span> from your client list? This cannot be undone.
              </div>
              <div style={styles.modalButtons}>
                <button style={styles.modalCancel} onClick={() => setConfirmModal(null)}>Cancel</button>
                <button style={styles.modalConfirm} onClick={handleDeleteConfirmed}>Remove Client</button>
              </div>
            </div>
          </div>
        )}

        <div style={styles.topbar}>
          <div>
            <div style={styles.pageTitle}>My Clients</div>
            <div style={styles.pageSub}>
              {loading ? 'Loading...' : `${filtered.length} of ${clients.length} clients`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              style={{ ...styles.addBtn, backgroundColor: '#1D6FA4' }}
              onClick={() => navigate('/import-clients')}>
              Import CSV
            </button>
            <button
              style={{ ...styles.addBtn, backgroundColor: '#0A2D5A' }}
              onClick={exportClientsCSV}>
              Export CSV
            </button>
            <button style={styles.addBtn} onClick={() => navigate('/add-client')}>
              + Add Client
            </button>
          </div>
        </div>

        <div style={styles.controls}>
          <input
            style={styles.searchInput}
            placeholder="Search clients..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {['all', 'Supplement', 'Advantage'].map(f => (
            <button key={f}
              style={filter === f ? styles.filterBtnActive : styles.filterBtn}
              onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>

        <div style={styles.legend}>
          <span style={styles.legendItem}>
            <span style={{ ...styles.legendDot, backgroundColor: '#2E8B57' }} />
            Client has installed the app
          </span>
          <span style={styles.legendItem}>
            <span style={{ ...styles.legendDot, backgroundColor: '#CC0000' }} />
            Not installed yet
          </span>
          <span style={styles.legendItem}>
            <span style={{ ...styles.legendDot, backgroundColor: '#1D6FA4' }} />
            Initial email sent
          </span>
        </div>

        <div style={styles.clientsCard}>
          {filtered.length === 0 && !loading && (
            <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 14 }}>
              {clients.length === 0 ? 'No clients yet. Add your first client.' : 'No clients match your search.'}
            </div>
          )}
          {filtered.map(client => (
            <div key={client.id} style={styles.clientRow}>
              <div
                title={client.appInstalled ? 'App installed' : 'Not installed yet'}
                style={{ ...styles.installDot, backgroundColor: client.appInstalled ? '#2E8B57' : '#CC0000' }}
              />
              {client.email && client.email.trim() && (
                <div
                  title={client.linkEmailedAt ? 'Initial email sent' : 'Initial email not sent yet'}
                  style={{ ...styles.emailDot, backgroundColor: client.linkEmailedAt ? '#1D6FA4' : '#ccc' }}
                />
              )}
              <div style={styles.avatar}>{getInitials(client.name)}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.clientName}>{client.name}</div>
                <div style={styles.clientSub}>{client.carrier} · {client.state}</div>
                <div style={styles.clientCode}>
                  <span style={styles.clientCodeLabel}>Code: </span>
                    {client.lookupCode
                    ? <span style={styles.clientCodeValue}>{client.lookupCode}</span>
                    : <span style={{ color: '#aaa', fontStyle: 'italic', fontWeight: '400' }}>generating…</span>}
                </div>
              </div>
              <span style={{
                ...styles.planBadge,
                backgroundColor: client.planType === 'Advantage' ? '#FDF0E8' : '#E6F1F9',
                color: client.planType === 'Advantage' ? '#993C1D' : '#185FA5',
              }}>
                {client.planType}
              </span>
              {client.planType === 'Advantage' && (
                <AEPStatusBadge
                  clientId={client.id}
                  status={client.aepStatus || ''}
                  onStatusChange={handleStatusChange}
                />
              )}
              <button
                style={{ ...styles.actionBtn, backgroundColor: '#2E7D32', color: 'white', border: '1px solid #2E7D32' }}
                onClick={() => copyAppLink(client.lookupCode, client.name)}>
                Copy App Link
              </button>
              <button
                style={styles.actionBtn}
                onClick={() => navigate(`/edit-client/${client.id}`)}>
                Edit
              </button>
              <button
                style={styles.deleteBtn}
                onClick={() => setConfirmModal({ clientId: client.id, name: client.name })}>
                Remove
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}



